import type { CustomField, CustomFieldValue } from "@web-app/types";

import {
  extractCustomFieldId,
  isCustomField,
} from "../../settings/FormBuilderTab/constants";
import type { OrderFormField } from "../../settings/FormBuilderTab/types";

export type CustomFieldState = {
  definition: CustomField;
  values: CustomFieldValue[];
};

// Only these types are rendered by ConfigDrivenTab, so only they can be filled in.
const RENDERED_TYPES = new Set<CustomField["type"]>([
  "TEXT",
  "NUMBER",
  "DROPDOWN",
]);

const isBlank = (value: CustomFieldValue["value"]) =>
  value === undefined || value === null || String(value).trim() === "";

export const hasCustomFieldValue = (
  definition: CustomField,
  values: CustomFieldValue[] | undefined,
) => {
  if (definition.type === "DROPDOWN") {
    return (values ?? []).some((v) => v.isSelected && !isBlank(v.value));
  }
  return (values ?? []).some((v) => !isBlank(v.value));
};

export const getDefaultCustomFieldState = (
  fields: OrderFormField[],
  definitions: CustomField[],
): CustomFieldState[] => {
  const definitionsById = new Map(definitions.map((d) => [d.id, d]));

  return fields.flatMap((field) => {
    if (!isCustomField(field.fieldKey)) return [];
    const definition = definitionsById.get(
      extractCustomFieldId(field.fieldKey),
    );
    if (
      !definition ||
      !hasCustomFieldValue(definition, definition.defaultValues)
    )
      return [];
    return [{ definition, values: definition.defaultValues }];
  });
};

export const getRequiredCustomFieldErrors = (
  fields: OrderFormField[],
  definitions: CustomField[],
  state: CustomFieldState[],
): Record<string, string> => {
  const definitionsById = new Map(definitions.map((d) => [d.id, d]));
  const stateById = new Map(state.map((s) => [s.definition.id, s]));

  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (!field.isRequired || !isCustomField(field.fieldKey)) continue;
    const id = extractCustomFieldId(field.fieldKey);
    const definition = definitionsById.get(id);
    if (!definition || !RENDERED_TYPES.has(definition.type)) continue;
    const entry = stateById.get(id);
    if (!hasCustomFieldValue(definition, entry?.values)) {
      errors[id] = `${definition.name} is required`;
    }
  }
  return errors;
};
